import { useEffect, useState } from "react";
import { Form, Button, Table, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faCheckDouble,
  faCircleXmark,
  faMagnifyingGlass,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import useSidebar from "../../../hooks/useSidebar";
import XButton from "../../../component/button/XButton";
import { API_ENDPOINTS } from "../../../constants/api";
import useAxios from "../../../hooks/useAxios";
import { useLocation, useNavigate } from "react-router-dom";
import PaginationNav from "../../../component/pagination/pagination";
import AsyncSelect from "react-select/async";
import { Option } from "../../../utils/interface";
import { debounceAsync } from "../../../utils/helper";
import { ActionMeta } from "react-select";
import { useFormik } from "formik";
import { toaster } from "../../../utils/toast";
import XLoader from "../../../component/loader/XLoader";
import XDeleteModal from "../../../component/modal/XDeleteModal";

const TagDetails = () => {
  const { onShowSidebar } = useSidebar();
  const location = useLocation();
  const { tagId, tagName } = location.state || {};
  const { fetchData, postData, isLoading } = useAxios();
  const [assignedContactsList, setAssignedContactsList] = useState([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [queryParams, setQueryParams] = useState({
    search: "",
    page: 1,
    limit: 10,
  });
  const [assignContactsModal, setAssignContactsModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<any>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const navigate = useNavigate();
  const [showRemoveAssignedContactModal, setShowRemoveAssignedContactModal] =
    useState(false);
  const [selectedContact, setSelectedContact] = useState<any>("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignProgress, setAssignProgress] = useState(0);

  const formik = useFormik({
    initialValues: {
      contacts: [],
    },
    onSubmit: async (values) => {
      try {
        setIsAssigning(true);
        setAssignProgress(10);
        const interval = setInterval(() => {
          setAssignProgress((prev) => (prev >= 95 ? 95 : prev + Math.max(1, Math.floor((96 - prev) * 0.15))));
        }, 200);

        const contactIds = selectedOptions.map((option: any) => option.value);
        const payload = {
          tagId: tagId,
          contactIds: contactIds,
        };

        const response = await postData(
          API_ENDPOINTS.ASSIGN_CONTACTS_TO_TAG,
          payload
        );
        clearInterval(interval);

        if (response.success) {
          setAssignProgress(100);
          setTimeout(() => {
            setAssignContactsModal(false);
            setSelectedOptions([]);
            setIsAssigning(false);
            setAssignProgress(0);
            getAssignedTagContacts();
          }, 400);
        } else {
          setIsAssigning(false);
          setAssignProgress(0);
        }
        toaster({ message: response.message, success: response.success });
      } catch (error: any) {
        setIsAssigning(false);
        setAssignProgress(0);
        toaster({ success: false, message: "Something went wrong" });
      }
    },
  });

  useEffect(() => {
    getAssignedTagContacts();
  }, [queryParams]);

  const getAssignedTagContacts = async () => {
    try {
      const { search, page, limit } = queryParams;
      let query = `${API_ENDPOINTS.GET_ASSIGNED_TAG_CONTACTS}/${tagId}?page=${page}&limit=${limit}`;
      if (search) {
        query += `&search=${search}`;
      }
      const response = await fetchData(query);
      const totalAssignedContacts = response?.data?.totalCount;
      const totalNoOfPages = Math.ceil(
        totalAssignedContacts / queryParams?.limit
      );
      setTotalPages(totalNoOfPages);
      setAssignedContactsList(response?.data?.assignedContactsList);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleQueryChange = (key: string, value: string | number) => {
    setQueryParams((prevParams) => ({ ...prevParams, [key]: value }));
  };

  const handleSelectChange = (
    selected: any,
    actionMeta: ActionMeta<Option>
  ) => {
    if (
      actionMeta.action === "select-option" &&
      selected &&
      selected[0].value === "all"
    ) {
      setSelectedOptions(options);
    } else setSelectedOptions(selected);
  };

  const getAllContacts = async (value?: string) => {
    try {
      if (!value) {
        return [];
      }
      const payload = {
        limit: 10,
        searchEmailString: value,
        excludeIds: [],
      };
      payload.excludeIds = selectedOptions.map(
        (option: { value: string }) => +option.value
      );
      const res = await postData(API_ENDPOINTS.GET_ALL_CONTACTS, payload);
      const data = res.contactList;
      const formattedOptions: Option[] = data.map((item: any) => ({
        value: item.id.toString(),
        label: item.email,
      }));
      setOptions(formattedOptions);
      return formattedOptions;
    } catch (error: any) {
      return [];
    }
  };

  const debouncedGetAllContacts = debounceAsync(getAllContacts, 600);

  const handleDeleteIconClick = async (contact: any) => {
    setShowRemoveAssignedContactModal(true);
    setSelectedContact(contact);
  };

  const handleRemoveAssignedContact = async (contactId: any, tagId: any) => {
    const response = await postData(
      `${API_ENDPOINTS.REMOVE_CONTACT_FROM_TAG}/${tagId}/${contactId}`
    );
    toaster({ message: response.message, success: response.success });
    getAssignedTagContacts();
  };

  const handleSelectAll = () => {
    setSelectedOptions((prevSelectedOptions: any) => [
      ...prevSelectedOptions,
      ...options,
    ]);
    setOptions([]);
  };

  return (
    <div>
      <div className="main-title d-flex align-items-center justify-content-between main-title-block">
        <div>
          <h2 className="d-flex align-items-center">
            <span
              className="d-lg-none cursor-pointer"
              onClick={() => onShowSidebar()}
            >
              <FontAwesomeIcon icon={faBars} />
            </span>
            Assigned to {tagName}
          </h2>
          <p className="mb-0">
            <span onClick={() => navigate("/org/settings/tags")}>
              <FontAwesomeIcon icon={faPlay} /> Back to all tags
            </span>
          </p>
        </div>
        <div className="filter-bar d-flex align-items-center row-item top-block">
          <div className="position-relative search-bar">
            <Form.Control
              type="search"
              placeholder="Search"
              aria-label="Search"
              value={queryParams.search}
              onChange={(e) => handleQueryChange("search", e.target.value)}
            />
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </div>
          <XButton
            buttonText="Assign Contacts"
            className="btn-theme"
            onClick={() => {
              setAssignContactsModal(true);
            }}
          />
        </div>
      </div>

      <div className="table-listing all-contacts users-list position-relative">
        <Table responsive>
          <thead>
            <tr>
              <th>Email</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {!isLoading && assignedContactsList?.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <p className="text-center no-records">
                    No assigned contacts found.
                  </p>
                </td>
              </tr>
            )}
            {!isLoading &&
              assignedContactsList?.map((contact: any) => (
                <tr key={contact.id}>
                  <td>{contact.email}</td>
                  <td className="actions">
                      <span>
                        <FontAwesomeIcon icon={faCircleXmark} onClick={() => handleDeleteIconClick(contact)}/>
                      </span>
                  </td>
                </tr>
              ))}
            {isLoading && <XLoader />}
          </tbody>
        </Table>
      </div>
      <Modal
        show={assignContactsModal}
        onHide={() => {
          setAssignContactsModal(false);
          setSelectedOptions([]);
        }}
        centered
      >
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Assign Contacts</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group
              className="form-group"
              controlId="exampleForm.ControlInput1"
            >
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label>Select Contacts To Assign</Form.Label>
                <Button
                  onClick={handleSelectAll}
                  className="csv-download-btn-style"
                  disabled={options.length === 0}
                >
                  <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
                  Select All Contacts
                </Button>
              </div>
              <AsyncSelect
                classNamePrefix="contacts-select"
                value={selectedOptions}
                onChange={handleSelectChange}
                isMulti
                defaultOptions
                loadOptions={debouncedGetAllContacts}
                placeholder="Search contacts here"
                styles={{
                  valueContainer: (provided: any) => ({
                    ...provided,
                    maxHeight: '160px',
                    overflowY: 'auto',
                  }),
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              variant="primary"
              className="btn-theme btn-no-border"
              disabled={isAssigning}
              onClick={() => {
                setAssignContactsModal(false);
                setSelectedOptions([]);
                setIsAssigning(false);
                setAssignProgress(0);
              }}
            >
              Close
            </Button>
            <XButton
              variant="primary"
              type="submit"
              buttonText="Submit"
              disabled={selectedOptions.length === 0 || isAssigning}
              loading={isAssigning}
              loadingText={`${assignProgress}% Processing...`}
            />
          </Modal.Footer>
        </Form>
      </Modal>

      <XDeleteModal
        show={showRemoveAssignedContactModal}
        handleClose={() => setShowRemoveAssignedContactModal(false)}
        title="Remove Contact"
        body={
          <p>
            Are you sure you want to remove the
            <span style={{ fontWeight: "bold" }}>
              {" "}
              {selectedContact?.email}
            </span>{" "}
            contact from this tag?
          </p>
        }
        onConfirm={() => {
          if (selectedContact) {
            handleRemoveAssignedContact(
              selectedContact.contact_id,
              selectedContact.tag_id
            );
          }
          setShowRemoveAssignedContactModal(false);
        }}
        onCancel={() => setShowRemoveAssignedContactModal(false)}
      />

      <PaginationNav
        totalPages={totalPages}
        currentPage={queryParams.page}
        handlePageChange={(page) => handleQueryChange("page", page)}
      />
    </div>
  );
};

export default TagDetails;
